import { normalizeString } from "@/utils/normalize-string";
import type { ImportDealResponse } from "../types";

/**
 * Plans how to apply the full AI brief (arrives async, after the fast
 * deterministic import) onto the diagnosis form without destroying input:
 *
 * - `fills` — values for fields that are currently EMPTY; safe to apply.
 * - `suggestions` — AI disagrees with a non-empty field (CRM data or user
 *   input); surfaced as a dismissible hint, never auto-applied.
 *
 * Pure function so the rules stay unit-testable outside the component.
 */

export interface BriefSuggestion {
  field: "city" | "capacity";
  /** Portuguese copy shown in the dismissible hint. */
  message: string;
  apply: {
    city?: string;
    positionsMin?: number;
    positionsMax?: number;
  };
}

export interface BriefHydrationForm {
  needs: string;
  companyName: string;
  contactName: string;
  contactEmail: string;
  segment: string;
  positionsMin: number | "";
  positionsMax: number | "";
  targetCity: string;
}

export interface BriefHydrationPlan {
  fills: {
    needs?: string;
    companyName?: string;
    contactName?: string;
    contactEmail?: string;
    segment?: string;
    positionsMin?: number;
    positionsMax?: number;
    city?: string;
  };
  suggestions: BriefSuggestion[];
}

/**
 * HardConstraints cannot represent "unknown capacity": the backend defaults
 * both bounds to 1, so null/null and 1/1 mean "missing" (same sentinel the
 * import prefill uses).
 */
function resolveBriefCapacity(
  brief: ImportDealResponse,
): { min: number; max: number } | null {
  const min = brief.hard_constraints.min_capacity;
  const max = brief.hard_constraints.max_capacity;
  if (min == null && max == null) return null;
  if (min === 1 && max === 1) return null;
  return { min: min ?? 1, max: max ?? min ?? 1 };
}

export function planBriefHydration(
  brief: ImportDealResponse,
  form: BriefHydrationForm,
  segmentOptions: string[],
): BriefHydrationPlan {
  const fills: BriefHydrationPlan["fills"] = {};
  const suggestions: BriefSuggestion[] = [];

  if (brief.summary_narrative && !form.needs.trim()) {
    fills.needs = brief.summary_narrative;
  }
  if (brief.company_name && !form.companyName.trim()) {
    fills.companyName = brief.company_name;
  }
  if (brief.primary_contact_email && !form.contactEmail.trim()) {
    fills.contactEmail = brief.primary_contact_email;
  }
  if (brief.primary_contact_name && !form.contactName.trim()) {
    fills.contactName = brief.primary_contact_name;
  }

  const industry = brief.industry?.trim();
  if (industry && !form.segment) {
    const matched = segmentOptions.find(
      (option) => normalizeString(option) === normalizeString(industry),
    );
    if (matched) fills.segment = matched;
  }

  const capacity = resolveBriefCapacity(brief);
  const formHasCapacity = form.positionsMin !== "" || form.positionsMax !== "";
  if (capacity && !formHasCapacity) {
    fills.positionsMin = capacity.min;
    fills.positionsMax = capacity.max;
  } else if (
    capacity &&
    formHasCapacity &&
    (form.positionsMin !== capacity.min || form.positionsMax !== capacity.max)
  ) {
    suggestions.push({
      field: "capacity",
      message: `A análise IA identificou capacidade ${capacity.min}–${capacity.max} posições (formulário: ${form.positionsMin || "?"}–${form.positionsMax || "?"}).`,
      apply: { positionsMin: capacity.min, positionsMax: capacity.max },
    });
  }

  const aiCity = brief.hard_constraints.city?.trim();
  if (aiCity) {
    const formCity = form.targetCity.trim();
    if (!formCity) {
      fills.city = aiCity;
    } else if (!normalizeString(formCity).includes(normalizeString(aiCity))) {
      // The form holds a full location label ("Av. Paulista - São Paulo");
      // only flag a divergence when the AI city isn't contained in it.
      suggestions.push({
        field: "city",
        message: `A análise IA identificou a cidade "${aiCity}" nos engagements (formulário: "${formCity}").`,
        apply: { city: aiCity },
      });
    }
  }

  return { fills, suggestions };
}
